import React from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import './SubscriptionBadge.css';

/**
 * Subscription Badge Component
 *
 * Displays subscription status and trial countdown
 * Shows prominently when trial is ending soon
 *
 * Usage:
 *   <SubscriptionBadge />
 */
export function SubscriptionBadge() {
  const {
    hasSubscription,
    isTrialing,
    daysRemaining,
    status,
    isActive,
    trialEndingSoon,
    loading,
    startCheckout,
  } = useSubscription();

  if (loading) {
    return null;
  }

  if (!hasSubscription) {
    // No subscription - show CTA to start trial
    return (
      <div className="subscription-badge subscription-badge--none">
        <span className="subscription-badge__icon">✨</span>
        <span className="subscription-badge__text">Start je 7-dagen gratis trial</span>
        <button
          className="subscription-badge__cta"
          onClick={() => startCheckout('personal')}
        >
          Start Trial
        </button>
      </div>
    );
  }

  if (isTrialing) {
    // Trial active
    const badgeClass = trialEndingSoon
      ? 'subscription-badge subscription-badge--warning'
      : 'subscription-badge subscription-badge--trial';

    return (
      <div className={badgeClass}>
        <span className="subscription-badge__icon">
          {trialEndingSoon ? '⚠️' : '🎯'}
        </span>
        <span className="subscription-badge__text">
          Trial: {daysRemaining} {daysRemaining === 1 ? 'dag' : 'dagen'} over
        </span>
        {trialEndingSoon && (
          <button
            className="subscription-badge__cta subscription-badge__cta--urgent"
            onClick={() => startCheckout('personal')}
          >
            Activeer Nu
          </button>
        )}
      </div>
    );
  }

  if (isActive && status === 'active') {
    // Paid subscription active
    return (
      <div className="subscription-badge subscription-badge--active">
        <span className="subscription-badge__icon">✅</span>
        <span className="subscription-badge__text">Personal Plan</span>
      </div>
    );
  }

  if (status === 'past_due') {
    // Payment failed
    return (
      <div className="subscription-badge subscription-badge--error">
        <span className="subscription-badge__icon">❌</span>
        <span className="subscription-badge__text">Betaling mislukt</span>
        <button
          className="subscription-badge__cta subscription-badge__cta--urgent"
          onClick={() => startCheckout('personal')}
        >
          Vernieuw
        </button>
      </div>
    );
  }

  if (status === 'canceled') {
    // Subscription canceled
    return (
      <div className="subscription-badge subscription-badge--canceled">
        <span className="subscription-badge__icon">🔒</span>
        <span className="subscription-badge__text">Abonnement beëindigd</span>
        <button
          className="subscription-badge__cta"
          onClick={() => startCheckout('personal')}
        >
          Heractiveer
        </button>
      </div>
    );
  }

  // Default state
  return null;
}

export default SubscriptionBadge;
